import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle token refresh on 401
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        const response = await axios.post(`${API_BASE_URL}/api/auth/refresh`, {}, {
          headers: { Authorization: `Bearer ${refreshToken}` }
        });

        const { access_token } = response.data;
        localStorage.setItem('access_token', access_token);

        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return api(originalRequest);
      } catch (refreshError) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (userData) => api.post('/api/auth/register', userData),
  login: (credentials) => api.post('/api/auth/login', credentials),
  getProfile: () => api.get('/api/auth/profile'),
  updateProfile: (data) => api.put('/api/auth/profile', data),
  connectWallet: (walletAddress) => api.post('/api/auth/wallet/connect', { wallet_address: walletAddress }),
  loginGoogle: () => {
    window.location.href = `${API_BASE_URL}/api/login/google`;
  },
  loginGithub: () => {
    window.location.href = `${API_BASE_URL}/api/login/github`;
  },
};

// Voice Models API
export const voiceAPI = {
  getModels: (params) => api.get('/api/voice/models', { params }),
  getModel: (id) => api.get(`/api/voice/models/${id}`),
  getMyModels: () => api.get('/api/voice/my-models'),
  createModel: (formData) => api.post('/api/voice/models', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  synthesizeSpeech: (id, text) => api.post(`/api/voice/models/${id}/synthesize`, { text }),
  purchaseLicense: (id, licenseType, paymentToken) =>
    api.post(`/api/voice/models/${id}/license`, { license_type: licenseType, payment_token: paymentToken }),
  addReview: (id, rating, comment) =>
    api.post(`/api/voice/models/${id}/review`, { rating, comment }),
  getMyLicenses: () => api.get('/api/voice/my-licenses'),
};

// Marketplace API
export const marketplaceAPI = {
  getFeatured: () => api.get('/api/marketplace/featured'),
  search: (searchParams) => api.post('/api/marketplace/search', searchParams),
  getTrending: () => api.get('/api/marketplace/trending'),
  getCategories: () => api.get('/api/marketplace/categories'),
  getRecommendations: () => api.get('/api/marketplace/recommendations'),
  getTopCreators: () => api.get('/api/marketplace/creators'),
  getCreatorProfile: (username) => api.get(`/api/marketplace/creator/${username}`),
  getStats: () => api.get('/api/marketplace/stats'),
};

export default api;
